import { ProjectStatusEnum } from '../enums';
import { Project } from '../models';

export enum ProjectEditionStatus {
  STEP1 = 'STEP1',
  STEP2_BATTERY_CONFIGURATION_CHOICE = 'STEP2_BATTERY_CONFIGURATION_CHOICE',
  STEP2_BATTERY_CONFIGURATION_MANUAL = 'STEP2_BATTERY_CONFIGURATION_MANUAL',
  STEP2_BATTERY_SIZING_TOOL = 'STEP2_BATTERY_SIZING_TOOL',
  STEP2_BATTERY_SIZING_TOOL_COMPUTED = 'STEP2_BATTERY_SIZING_TOOL_COMPUTED',
  STEP3_ENCLOSURE_INTERCONNECTS = 'STEP3_ENCLOSURE_INTERCONNECTS',
  STEP4_WIRING_PLAN_BOM = 'STEP4_WIRING_PLAN_BOM',
  STEP4_COMPLETED = 'STEP4_COMPLETED',
}

export type ProjectStatusConfig = {
  readonly stepStatus: ProjectEditionStatus;
  readonly route: string;
  readonly isFinalStep: boolean;
  readonly getPrevious: (project?: Project) => ProjectEditionStatus | null;
};

const ProjectStatusConf: readonly ProjectStatusConfig[] = [
  {
    stepStatus: ProjectEditionStatus.STEP1,
    route: 'step-one',
    isFinalStep: false,
    getPrevious: (_project?: Project): ProjectEditionStatus | null => {
      void _project;
      return null;
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_CHOICE,
    route: 'step-two-configuration-method',
    isFinalStep: false,
    getPrevious: (_project?: Project): ProjectEditionStatus => {
      void _project;
      return ProjectEditionStatus.STEP1;
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_MANUAL,
    route: 'step-two-manual-configuration',
    isFinalStep: false,
    getPrevious: (_project?: Project): ProjectEditionStatus => {
      void _project;
      return ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_CHOICE;
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP2_BATTERY_SIZING_TOOL,
    route: 'step-two-sizing-power-voltage',
    isFinalStep: false,
    getPrevious: (_project?: Project): ProjectEditionStatus => {
      void _project;
      return ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_CHOICE;
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP2_BATTERY_SIZING_TOOL_COMPUTED,
    route: 'step-two-sizing-computed',
    isFinalStep: false,
    getPrevious: (_project?: Project): ProjectEditionStatus => {
      void _project;
      return ProjectEditionStatus.STEP2_BATTERY_SIZING_TOOL;
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP3_ENCLOSURE_INTERCONNECTS,
    route: 'step-three',
    isFinalStep: false,
    getPrevious: (project?: Project): ProjectEditionStatus => {
      if (project?.isManualSizing) {
        return ProjectEditionStatus.STEP2_BATTERY_CONFIGURATION_MANUAL;
      } else {
        return ProjectEditionStatus.STEP2_BATTERY_SIZING_TOOL_COMPUTED;
      }
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP4_WIRING_PLAN_BOM,
    route: 'step-four',
    isFinalStep: false,
    getPrevious: (_project?: Project): ProjectEditionStatus => {
      void _project;
      return ProjectEditionStatus.STEP3_ENCLOSURE_INTERCONNECTS;
    },
  },
  {
    stepStatus: ProjectEditionStatus.STEP4_COMPLETED,
    route: 'step-four',
    isFinalStep: true,
    getPrevious: (_project?: Project) => {
      void _project;
      return ProjectEditionStatus.STEP4_WIRING_PLAN_BOM;
    },
  },
];

export enum StepAccessibility {
  ToEdit = 'toEdit',
  AlreadyEdited = 'alreadyEdited',
  NotAccessible = 'not-accessible',
  ReadOnly = 'Readonly',
}

export interface StepAccessibilityResult {
  accessibility: StepAccessibility;
  redirectRoute?: string;
}

export class ProjectStatusHelper {
  // Retrieve step config based on a route fragment, preferring the config matching the current status when multiple share a route.
  private getConfigByRoute(
    route: string,
    projectStatus?: ProjectEditionStatus,
  ) {
    const matchingConfigs = ProjectStatusConf.filter((conf) =>
      this.isRouteMatching(conf.route, route),
    );

    if (!matchingConfigs.length) {
      return undefined;
    }

    return (
      matchingConfigs.find((conf) => conf.stepStatus === projectStatus) ??
      matchingConfigs[0]
    );
  }

  // Walk backward through the step chain to see if candidate is a previous status.
  private isPreviousStatus(
    baseStatus: ProjectEditionStatus,
    candidate?: ProjectEditionStatus,
    project?: Project | null,
  ): boolean {
    if (!candidate) {
      return false;
    }

    let previous = this.getConfig(baseStatus)?.getPrevious(
      project ?? undefined,
    );

    while (previous) {
      if (previous === candidate) {
        return true;
      }

      previous = this.getConfig(previous)?.getPrevious(project ?? undefined);
    }

    return false;
  }

  getConfig(stepStatus: ProjectEditionStatus) {
    return ProjectStatusConf.find((conf) => conf.stepStatus === stepStatus);
  }

  // Determine accessibility of a target step relative to the current route.
  getAcessibilityConfig(
    project: Project | null,
    currentRoute: string,
  ): StepAccessibilityResult {
    if (!project) {
      const targetConfig = this.getConfig(ProjectEditionStatus.STEP1);
      return {
        accessibility: StepAccessibility.ToEdit,
        redirectRoute: targetConfig?.route ?? '',
      };
    }

    if (project.isArchived) {
      return {
        accessibility: StepAccessibility.ReadOnly,
      };
    }

    if (
      project.projectStatus !== ProjectStatusEnum.DRAFT &&
      project.projectStatus !== ProjectStatusEnum.SIZING_COMPLETED
    ) {
      return {
        accessibility: StepAccessibility.ReadOnly,
      };
    }

    const stepStatus = project.editionStatus;
    if (!stepStatus) {
      return {
        accessibility: StepAccessibility.NotAccessible,
        redirectRoute: '',
      };
    }

    const targetConfig = this.getConfig(stepStatus);
    // Unknown target step => no access.
    if (!targetConfig) {
      return {
        accessibility: StepAccessibility.NotAccessible,
        redirectRoute: '',
      };
    }

    if (targetConfig.isFinalStep) {
      return {
        accessibility: StepAccessibility.AlreadyEdited,
      };
    }

    const currentConfig = this.getConfigByRoute(currentRoute, stepStatus);

    // Unknown current route => block and redirect to target step.
    if (!currentConfig) {
      return {
        accessibility: StepAccessibility.NotAccessible,
        redirectRoute: targetConfig.route,
      };
    }

    // Same step status as current route => editable.
    if (currentConfig.stepStatus === stepStatus) {
      return {
        accessibility: StepAccessibility.ToEdit,
      };
    }

    // Current route maps to a previous step => readable.
    if (this.isPreviousStatus(stepStatus, currentConfig.stepStatus, project)) {
      return {
        accessibility: StepAccessibility.AlreadyEdited,
      };
    }

    // Current route is ahead of target step => block and redirect to target route.
    if (this.isPreviousStatus(currentConfig.stepStatus, stepStatus, project)) {
      return {
        accessibility: StepAccessibility.NotAccessible,
        redirectRoute: targetConfig.route,
      };
    }

    return {
      accessibility: StepAccessibility.NotAccessible,
      redirectRoute: targetConfig.route,
    };
  }

  private isRouteMatching(route: string, currentRoute: string) {
    return currentRoute === route || currentRoute.includes(route);
  }
}
